// MCHD Room Scheduler - Session Table Component
// Reusable table component for displaying and managing user sessions

import { useState } from 'react';
import {
  Table,
  Badge,
  ActionIcon,
  Group,
  Text,
  Avatar,
  Button,
  Modal,
  Alert,
  Stack,
  Box,
  Tooltip,
  Pagination,
  TextInput,
  Select,
  Card,
  Grid,
} from '@mantine/core';
import {
  IconTrash,
  IconEye,
  IconSearch,
  IconFilter,
  IconAlertCircle,
  IconClock,
  IconDeviceDesktop,
  IconDeviceMobile,
  IconWifi,
  IconShield,
} from '@tabler/icons-react';
import { Session } from '../../../api/endpoints/auth';
import DynamicRefreshButton from '../../atoms/DynamicRefreshButton';

interface SessionTableProps {
  sessions: Session[];
  isLoading?: boolean;
  onTerminate: (session: Session) => void;
  onTerminateAll: () => void;
  onView: (session: Session) => void;
  onRefresh: () => void;
  stats: {
    total: number;
    active: number;
    inactive: number;
    recent: number;
    uniqueIPs: number;
    uniqueDevices: number;
  };
}

/**
 * Session table component with actions and filtering
 * @param props - Component props
 * @returns JSX element
 */
export function SessionTable({
  sessions,
  isLoading = false,
  onTerminate,
  onTerminateAll,
  onView,
  onRefresh,
  stats,
}: SessionTableProps): JSX.Element {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [deviceFilter, setDeviceFilter] = useState<string | null>(null);
  const [terminateModalOpen, setTerminateModalOpen] = useState(false);
  const [terminateAllModalOpen, setTerminateAllModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Filter sessions based on search and filters
  const filteredSessions = sessions.filter((session) => {
    const matchesSearch = 
      session.ipAddress.toLowerCase().includes(searchTerm.toLowerCase()) ||
      session.userAgent.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (session.deviceInfo && session.deviceInfo.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = !statusFilter || 
      (statusFilter === 'active' && session.isActive) ||
      (statusFilter === 'inactive' && !session.isActive);
    
    const matchesDevice = !deviceFilter || 
      (session.deviceInfo && session.deviceInfo.toLowerCase().includes(deviceFilter.toLowerCase()));
    
    return matchesSearch && matchesStatus && matchesDevice;
  });

  // Paginate sessions
  const totalPages = Math.ceil(filteredSessions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSessions = filteredSessions.slice(startIndex, startIndex + itemsPerPage);

  const getDeviceIcon = (userAgent: string) => {
    if (userAgent.includes('Mobile') || userAgent.includes('iPhone') || userAgent.includes('Android')) {
      return <IconDeviceMobile size={16} />;
    }
    return <IconDeviceDesktop size={16} />;
  };

  const getDeviceType = (userAgent: string) => {
    if (userAgent.includes('Mobile') || userAgent.includes('iPhone') || userAgent.includes('Android')) {
      return 'Mobile';
    }
    return 'Desktop';
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const getActivityStatus = (session: Session) => {
    const lastActivity = new Date(session.lastActivity);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - lastActivity.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 5) return { status: 'Very Recent', color: 'green' };
    if (diffInMinutes < 30) return { status: 'Recent', color: 'blue' };
    if (diffInMinutes < 120) return { status: 'Idle', color: 'yellow' };
    return { status: 'Inactive', color: 'red' };
  };

  const handleTerminateClick = (session: Session) => {
    setSelectedSession(session);
    setTerminateModalOpen(true);
  };

  const handleTerminateConfirm = () => {
    if (selectedSession) {
      onTerminate(selectedSession);
      setTerminateModalOpen(false);
      setSelectedSession(null);
    }
  };

  const handleTerminateAllClick = () => {
    setTerminateAllModalOpen(true);
  };

  const handleTerminateAllConfirm = () => {
    onTerminateAll();
    setTerminateAllModalOpen(false);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.currentTarget.value);
    setCurrentPage(1);
  };

  const handleFilterChange = () => {
    setCurrentPage(1);
  };

  if (isLoading) {
    return (
      <Box p="xl" ta="center">
        <Text c="dimmed">Loading sessions...</Text>
      </Box>
    );
  }

  return (
    <Stack gap="md">
      {/* Statistics Cards */}
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6, md: 2 }}>
          <Card shadow="sm" padding="sm" radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Text size="xs" fw={500} c="dimmed">Total</Text>
              <IconShield size={16} color="var(--mantine-color-blue-6)" />
            </Group>
            <Text size="lg" fw={700} c="blue">{stats.total}</Text>
          </Card>
        </Grid.Col>
        
        <Grid.Col span={{ base: 12, sm: 6, md: 2 }}>
          <Card shadow="sm" padding="sm" radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Text size="xs" fw={500} c="dimmed">Active</Text>
              <IconClock size={16} color="var(--mantine-color-green-6)" />
            </Group>
            <Text size="lg" fw={700} c="green">{stats.active}</Text>
          </Card>
        </Grid.Col>
        
        <Grid.Col span={{ base: 12, sm: 6, md: 2 }}>
          <Card shadow="sm" padding="sm" radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Text size="xs" fw={500} c="dimmed">Recent</Text>
              <IconWifi size={16} color="var(--mantine-color-blue-6)" />
            </Group>
            <Text size="lg" fw={700} c="blue">{stats.recent}</Text>
          </Card>
        </Grid.Col>
        
        <Grid.Col span={{ base: 12, sm: 6, md: 2 }}>
          <Card shadow="sm" padding="sm" radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Text size="xs" fw={500} c="dimmed">IPs</Text>
              <IconWifi size={16} color="var(--mantine-color-orange-6)" />
            </Group>
            <Text size="lg" fw={700} c="orange">{stats.uniqueIPs}</Text>
          </Card>
        </Grid.Col>
        
        <Grid.Col span={{ base: 12, sm: 6, md: 2 }}>
          <Card shadow="sm" padding="sm" radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Text size="xs" fw={500} c="dimmed">Devices</Text>
              <IconDeviceDesktop size={16} color="var(--mantine-color-purple-6)" />
            </Group>
            <Text size="lg" fw={700} c="purple">{stats.uniqueDevices}</Text>
          </Card>
        </Grid.Col>
        
        <Grid.Col span={{ base: 12, sm: 6, md: 2 }}>
          <Card shadow="sm" padding="sm" radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Text size="xs" fw={500} c="dimmed">Inactive</Text>
              <IconAlertCircle size={16} color="var(--mantine-color-red-6)" />
            </Group>
            <Text size="lg" fw={700} c="red">{stats.inactive}</Text>
          </Card>
        </Grid.Col>
      </Grid>

      {/* Search and Filter Controls */}
      <Group justify="space-between" wrap="wrap">
        <Group gap="md" wrap="wrap">
          <TextInput
            placeholder="Search sessions..."
            leftSection={<IconSearch size={16} />}
            value={searchTerm}
            onChange={handleSearchChange}
            style={{ minWidth: 250 }}
          />
          <Select
            placeholder="Filter by status"
            data={[
              { value: '', label: 'All Status' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              handleFilterChange();
            }}
            leftSection={<IconFilter size={16} />}
            clearable
          />
          <Select
            placeholder="Filter by device"
            data={[
              { value: '', label: 'All Devices' },
              { value: 'mobile', label: 'Mobile' },
              { value: 'desktop', label: 'Desktop' },
            ]}
            value={deviceFilter}
            onChange={(value) => {
              setDeviceFilter(value);
              handleFilterChange();
            }}
            leftSection={<IconFilter size={16} />}
            clearable
          />
        </Group>
        
        <Group gap="sm">
          <DynamicRefreshButton
            variant="outline"
            onRefresh={onRefresh}
            size="sm"
            color="blue"
          />
          <Button
            color="red"
            variant="outline"
            leftSection={<IconTrash size={16} />}
            onClick={handleTerminateAllClick}
            disabled={stats.active === 0}
          >
            Terminate All
          </Button>
        </Group>
      </Group>

      {/* Sessions Table */}
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Device</Table.Th>
            <Table.Th>IP Address</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Last Activity</Table.Th>
            <Table.Th>Created</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {paginatedSessions.length === 0 ? (
            <Table.Tr>
              <Table.Td colSpan={6} ta="center" py="xl">
                <Stack gap="sm" align="center">
                  <IconAlertCircle size={48} color="var(--mantine-color-dimmed)" />
                  <Text c="dimmed">No sessions found</Text>
                  <Text size="sm" c="dimmed">
                    {searchTerm || statusFilter || deviceFilter
                      ? 'Try adjusting your search or filters'
                      : 'No active sessions'}
                  </Text>
                </Stack>
              </Table.Td>
            </Table.Tr>
          ) : (
            paginatedSessions.map((session) => {
              const activityStatus = getActivityStatus(session);
              return (
                <Table.Tr key={session.id}>
                  <Table.Td>
                    <Group gap="sm">
                      <Avatar
                        size="md"
                        radius="xl"
                        color={getDeviceType(session.userAgent) === 'Mobile' ? 'blue' : 'green'}
                      >
                        {getDeviceIcon(session.userAgent)}
                      </Avatar>
                      <Box>
                        <Text fw={500} size="sm">
                          {getDeviceType(session.userAgent)}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {session.deviceInfo || 'Unknown Device'}
                        </Text>
                      </Box>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" ff="monospace">
                      {session.ipAddress}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Badge
                        color={session.isActive ? 'green' : 'red'}
                        variant="light"
                        size="sm"
                      >
                        {session.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      <Badge
                        color={activityStatus.color}
                        variant="light"
                        size="sm"
                      >
                        {activityStatus.status}
                      </Badge>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {getTimeAgo(session.lastActivity)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {new Date(session.lastActivity).toLocaleString()}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {new Date(session.createdAt).toLocaleDateString()}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Tooltip label="View details">
                        <ActionIcon
                          variant="subtle"
                          color="blue"
                          onClick={() => onView(session)}
                        >
                          <IconEye size={16} />
                        </ActionIcon>
                      </Tooltip>
                      {session.isActive && (
                        <Tooltip label="Terminate session">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => handleTerminateClick(session)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })
          )}
        </Table.Tbody>
      </Table>

      {/* Pagination */}
      {totalPages > 1 && (
        <Group justify="center" mt="md">
          <Pagination
            value={currentPage}
            onChange={setCurrentPage}
            total={totalPages}
            size="sm"
          />
        </Group>
      )}

      {/* Terminate Session Confirmation Modal */}
      <Modal
        opened={terminateModalOpen}
        onClose={() => setTerminateModalOpen(false)}
        title="Terminate Session"
        centered
      >
        <Stack gap="md">
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Are you sure?"
            color="red"
            variant="light"
          >
            This will immediately terminate the selected session. The user will be logged out.
          </Alert>
          
          {selectedSession && (
            <Box>
              <Text size="sm" fw={500}>Session Details:</Text>
              <Text size="sm" c="dimmed">
                <strong>Device:</strong> {getDeviceType(selectedSession.userAgent)}
              </Text>
              <Text size="sm" c="dimmed">
                <strong>IP Address:</strong> {selectedSession.ipAddress}
              </Text>
              <Text size="sm" c="dimmed">
                <strong>Last Activity:</strong> {getTimeAgo(selectedSession.lastActivity)}
              </Text>
            </Box>
          )}

          <Group justify="flex-end" gap="sm">
            <Button
              variant="outline"
              onClick={() => setTerminateModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleTerminateConfirm}
            >
              Terminate Session
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Terminate All Sessions Confirmation Modal */}
      <Modal
        opened={terminateAllModalOpen}
        onClose={() => setTerminateAllModalOpen(false)}
        title="Terminate All Sessions"
        centered
      >
        <Stack gap="md">
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Are you sure?"
            color="red"
            variant="light"
          >
            This will immediately terminate ALL active sessions. All users will be logged out.
          </Alert>
          
          <Box>
            <Text size="sm" fw={500}>This action will affect:</Text>
            <Text size="sm" c="dimmed">
              • {stats.active} active session{stats.active !== 1 ? 's' : ''}
            </Text>
            <Text size="sm" c="dimmed">
              • {stats.uniqueIPs} unique IP address{stats.uniqueIPs !== 1 ? 'es' : ''}
            </Text>
            <Text size="sm" c="dimmed">
              • {stats.uniqueDevices} unique device{stats.uniqueDevices !== 1 ? 's' : ''}
            </Text>
          </Box>

          <Group justify="flex-end" gap="sm">
            <Button
              variant="outline"
              onClick={() => setTerminateAllModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleTerminateAllConfirm}
            >
              Terminate All Sessions
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
